import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Grid2
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Policy as PolicyIcon,
  Info as InfoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const Privacy = () => {
  const theme = useTheme();
  const lastUpdated = "June 26, 2025";

  const privacySections = [
    {
      title: "Information We Collect",
      icon: <DataUsageIcon />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Personal Information
          </Typography>
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Account Information
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Name, email address, and profile picture when you create an account
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Event Data
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Event details, guest lists, budgets, tasks, and RSVP information you create
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Usage Information
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    How you interact with the app, features used, and performance data
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Device Information
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Device type, operating system, app version, and unique device identifiers
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      )
    },
    {
      title: "How We Use Your Information",
      icon: <InfoIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            We use the information we collect to:
          </Typography>
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Provide and improve our services
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Deliver the Event Planner app functionality and enhance user experience
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Enable collaboration
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Allow you to share events and collaborate with friends and family
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Send notifications
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Keep you informed about event updates, deadlines, and important reminders
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      )
    },
    {
      title: "Information Sharing",
      icon: <ShareIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
          </Typography>
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    With your consent
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    When you explicitly agree to share information with other users or third parties
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Service providers
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    With trusted third-party services that help us operate the app (hosting, analytics, etc.)
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      )
    },
    {
      title: "Data Security",
      icon: <SecurityIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            We implement industry-standard security measures to protect your information:
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 2,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(76, 175, 80, 0.05)',
                border: `1px solid ${theme.palette.success.main}30`,
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Encryption
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  All data is encrypted in transit and at rest using AES-256 encryption
                </Typography>
              </Card>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 2,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(33, 150, 243, 0.1)'
                  : 'rgba(33, 150, 243, 0.05)',
                border: `1px solid ${theme.palette.primary.main}30`,
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Secure Servers
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Data is stored on secure, monitored servers with regular security audits
                </Typography>
              </Card>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 2,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(156, 39, 176, 0.1)'
                  : 'rgba(156, 39, 176, 0.05)',
                border: `1px solid ${theme.palette.secondary.main}30`,
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VisibilityIcon sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Access Controls
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Strict access controls limit who can access your personal information
                </Typography>
              </Card>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 2,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 152, 0, 0.1)'
                  : 'rgba(255, 152, 0, 0.05)',
                border: `1px solid ${theme.palette.warning.main}30`,
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DeleteIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Data Retention
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  We retain your data only as long as necessary to provide our services
                </Typography>
              </Card>
            </Grid2>
          </Grid2>
        </Box>
      )
    },
    {
      title: "Changes to This Policy",
      icon: <PolicyIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of the app after such changes constitutes acceptance of the updated policy.
          </Typography>
        </Box>
      )
    },
    {
      title: "Contact Us",
      icon: <EmailIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 3,
                textAlign: 'center',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(33, 150, 243, 0.1)'
                  : 'rgba(33, 150, 243, 0.05)',
                border: `1px solid ${theme.palette.primary.main}30`,
                borderRadius: 2
              }}>
                <EmailIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Email
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                  krishnavn13@outlook.com
                </Typography>
              </Card>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Card sx={{
                p: 3,
                textAlign: 'center',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(76, 175, 80, 0.05)',
                border: `1px solid ${theme.palette.success.main}30`,
                borderRadius: 2
              }}>
                <PhoneIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Phone
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                  +1 (469) 573-1676
                </Typography>
              </Card>
            </Grid2>
          </Grid2>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Header Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          mt: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 2,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            <PolicyIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Privacy Policy
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ 
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            How we collect, use, and protect your information
          </Typography>
          <Chip 
            label={`Last Updated: ${lastUpdated}`} 
            color="primary" 
            variant="outlined"
            sx={{ 
              fontWeight: 500,
              fontSize: '0.9rem',
              px: 2,
              py: 1
            }}
          />
        </Box>

        {/* Introduction */}
        <Card sx={{
          mb: 4,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.2)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Introduction
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 2, lineHeight: 1.6 }}>
              Event Planner ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Event Planner mobile application and related services.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
              By using our app, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our app.
            </Typography>
          </CardContent>
        </Card>

        {/* Privacy Sections */}
        <Box sx={{ mb: 4 }}>
          {privacySections.map((section, index) => (
            <Accordion 
              key={index} 
              defaultExpanded={index < 2} 
              sx={{ 
                mb: 2,
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.2)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {React.cloneElement(section.icon, { 
                    sx: { 
                      mr: 2, 
                      color: theme.palette.primary.main,
                      fontSize: { xs: 24, sm: 28 }
                    } 
                  })}
                  <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                {section.content}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Additional Information */}
        <Card sx={{
          mt: 4,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.2)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Additional Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 2, lineHeight: 1.6 }}>
              This Privacy Policy is effective as of the date listed above and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
              For users in the European Union, this policy also serves as our GDPR compliance statement. For users in California, this policy includes information about your rights under the California Consumer Privacy Act (CCPA).
            </Typography>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            © 2024 Event Planner Inc. All rights reserved.
          </Typography>
          <Link 
            href="/support" 
            color="primary" 
            sx={{ 
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Support Center
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Privacy; 